<template>
    <div class="flex flex-row w-full h-full">
        <div class="flex-none p-4 border-r h-full flex flex-col">
            <div class="mb-4 flex flex-row gap-2 items-center">
                <img src="@/assets/broker_market_small.png" alt="Logo" class="h-10 w-auto" />
                <Button text class="ml-auto relative" @click="handleNotifications">
                    <i class="pi pi-bell" />
                    <span class="absolute -top-0 -right-0 rounded-full text-xs w-4 h-4 flex items-center justify-center">2</span>
                </Button>
            </div>
            <div class="flex flex-col">
                <Button label="Markedspakke" icon="pi pi-file" text />
                <Button label="Merktantilt/malbank" icon="pi pi-th-large" text />
                <Button label="Mediabanken" icon="pi pi-images" text />
                <Button label="Kjøp telefonnummer" icon="pi pi-phone" text />
                <Button label="Kampanjer" icon="pi pi-megaphone" text />
            </div>
            <legend class="text-sm font-bold mt-4">En eller annen legend</legend>
            <div class="flex flex-col">
                <Button label="ePaper v2" icon="pi pi-book" text />
                <Button label="Statistikk" icon="pi pi-chart-line" text />
            </div>

            <legend class="text-sm font-bold mt-4">Annet</legend>
            <div class="flex flex-col">
                <Button label="Webmegler" icon="pi pi-globe" text />
                <Button label="Innstillinger" icon="pi pi-cog" text />
            </div>
            <div class="mt-auto">
                <div class="flex flex-row gap-2 items-center mb-2">
                    <Avatar image="https://github.com/garlov.png" size="large" shape="circle" />
                    <div class="grid grid-cols-1">
                        <span class="font-bold">
                            {{
                                userSessionStore.getUser
                                    ? `${userSessionStore.getUser.firstName} ${userSessionStore.getUser.lastName}`
                                    : 'Loading...'
                            }}
                        </span>
                        <span class="text-xs">
                            {{ userSessionStore.getUser?.email || 'No email' }}
                        </span>
                    </div>
                </div>
                <Button label="Logg ut" icon="pi pi-sign-out" text />
            </div>
        </div>
        <main class="w-11/12 p-4 h-100">
            <div class="flex gap-4 h-100">
                <div class="w-1/6">
                    <Card class="h-auto">
                        <template #title>
                            <h3 class="text-lg font-semibold">Mine Salgsobjekter</h3>
                        </template>
                        <template #content>
                            <div>
                                <ResObjectSelector :res-objects="userSessionStore.getResObjects" @res-object-click="handleResObjectClick" />
                            </div>
                        </template>
                    </Card>
                </div>
                <div class="grow">
                    <!-- Router View for nested routes -->
                    <RouterView v-slot="{ Component }">
                        <component :is="Component" v-if="route.name !== 'home'" />
                        <!-- Default dashboard view when no resource is selected -->
                        <div v-else class="h-full flex flex-col">
                            <!-- Loading State -->
                            <div v-if="userSessionStore.isLoading" class="flex align-items-center justify-content-center p-4">
                                <LoadingSpinner />
                                <span class="ml-2">Loading session...</span>
                            </div>

                            <!-- Error State -->
                            <Message v-else-if="userSessionStore.hasError" severity="error" :closable="false">
                                <strong>Error loading session:</strong>
                                {{ userSessionStore.getError }}
                                <Button label="Retry" text severity="secondary" class="ml-2" @click="initializeSession" />
                            </Message>

                            <!-- Welcome Message -->
                            <div v-else-if="userSessionStore.isLoggedIn" class="space-y-4">
                                <Card>
                                    <template #content>
                                        <div class="flex flex-column gap-3">
                                            <div v-if="userSessionStore.getUser" class="text-xl font-semibold">
                                                Welcome, {{ userSessionStore.getUser.firstName }}
                                                {{ userSessionStore.getUser.lastName }}
                                            </div>
                                            <div v-else class="text-xl font-semibold">Welcome, you are not logged in as a user.</div>

                                            <div
                                                v-if="userSessionStore.isImpersonating && userSessionStore.getTopLevelUser"
                                                class="flex align-items-center gap-2"
                                            >
                                                <Tag
                                                    severity="warning"
                                                    :value="`Impersonated by ${userSessionStore.getTopLevelUser.firstName} ${userSessionStore.getTopLevelUser.lastName}`"
                                                />
                                            </div>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- No Session State -->
                            <Message v-else severity="info" :closable="false"> No session data available </Message>
                        </div>
                    </RouterView>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { Tag, Card, Message, Button, Avatar } from 'primevue';
import { useUserSessionStore } from '../stores/userSession';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import ResObjectSelector from '../components/ResObjectSelector.vue';
import { ResObject } from '@visma-real-estate-solutions/spring-editor-shared';

// User session store
const userSessionStore = useUserSessionStore();

// Router and toast service
const route = useRoute();
const router = useRouter();
const toast = useToast();

// Initialize session
const initializeSession = async () => {
    try {
        await userSessionStore.initializeSession();
    } catch (error) {
        console.error('Failed to initialize session:', error);

        toast.add({
            severity: 'error',
            summary: 'Session Error',
            detail: 'Failed to initialize session',
            life: 5000,
        });
    }
};

// Handle notifications
const handleNotifications = () => {
    console.log('Notifications clicked');
    // Add notification handling logic here
};

// Handle ResObject click
const handleResObjectClick = (resObject: ResObject) => {
    console.log('ResObject clicked:', resObject);
    router.push(`/res/${resObject.id}`);
};

// Initialize session on component mount
onMounted(() => {
    initializeSession();
});
</script>

<style scoped>
.h-100 {
    height: 100vh;
}

.w-1\/6 {
    width: 16.666667%;
}

.w-11\/12 {
    width: 91.666667%;
}

.grow {
    flex-grow: 1;
}
</style>
